import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import Drawer from '../../commons/drawer/Drawer';
import DateTimeField from '../../commons/datetime/DateTimeField';
import MoneyInput from '../../commons/money-input/MoneyInput';
import SearchableSelect from '../../commons/searchable-select/SearchableSelect';
import { useAlert } from '../../../context/alert/AlertContext';
import { useConfirm } from '../../../context/confirm/ConfirmContext';
import { formatMoney, currencyCode } from '../../../util/money';
import { formatBizDateTime, formatClock, useTimeFormat } from '../../../util/timeFormat';
import { Service } from '../../../model/service/Service';
import { TeamMember } from '../../../model/teammember/TeamMember';
import { Client } from '../../../model/client/Client';
import { Event, EventInput, EventMemberConflict, EventParticipant } from '../../../model/event/Event';
import {
    addEventParticipant,
    cancelEvent,
    checkEventConflicts,
    createEvent,
    getEventById,
    removeEventParticipant,
    updateEvent,
} from '../../../service/event/event-service';
import { getAllClientsByBusinessId } from '../../../service/client/client-service';
import { formatPostal, isCompletePostal, lookupAddress, postalPlaceholder, canonicalCountry } from '../../../util/cep';
import { recordManualPayment } from '../../../service/payment/transaction-service';
import { ONLINE_PAYMENTS_ENABLED } from '../../../util/onlinePayments';
import './EventDrawer.css';

interface Props {
    open: boolean;
    /** create mode when null; otherwise the event being viewed/edited. */
    eventId: string | null;
    /** Prefilled start (ISO local) when creating from a calendar slot. */
    initialStart?: string | null;
    businessId: string;
    currency?: string | null;
    country?: string | null;
    /** Event-type catalog items (Service.kind === 'EVENT'). */
    eventTypes: Service[];
    teamMembers: TeamMember[];
    stepMinutes?: number;
    onClose: () => void;
    /** Called after any change that should refresh the calendar. */
    onChanged: () => void;
}

/** Add minutes to an ISO-local "yyyy-MM-ddTHH:mm(:ss)" string. */
const addMinutes = (iso: string, minutes: number): string => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    d.setMinutes(d.getMinutes() + minutes);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const clientLabel = (c: Client) => `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();

/**
 * Dedicated drawer for group events / workshops — SEPARATE from the appointment
 * drawer. Create: pick an event-type, time, and a LIST of professionals with a
 * live conflict check; a public/private toggle. View/edit: participant list
 * (with client lookup + manual entry) + per-person payment + money rollup.
 */
const EventDrawer: React.FC<Props> = ({
    open, eventId, initialStart, businessId, currency, country, eventTypes, teamMembers,
    stepMinutes, onClose, onChanged,
}) => {
    const { t, i18n } = useTranslation();
    const timeFmt = useTimeFormat();
    const { getAccessTokenSilently } = useAuth0();
    const { setAlert } = useAlert();
    const confirmDialog = useConfirm();

    const [event, setEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    /** View mode → true switches the drawer to the full edit form. */
    const [isEditing, setIsEditing] = useState(false);

    // Create-mode draft.
    const [serviceId, setServiceId] = useState('');
    const [title, setTitle] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [capacity, setCapacity] = useState<number | null>(null);
    const [defaultPrice, setDefaultPrice] = useState<number>(0);
    const [isPublic, setIsPublic] = useState(false);
    const [depositRequired, setDepositRequired] = useState(false);
    const [depositAmount, setDepositAmount] = useState<number | null>(null);
    const [notes, setNotes] = useState('');
    const [hasLocation, setHasLocation] = useState(false);
    const [addrCep, setAddrCep] = useState('');
    const [addrStreet, setAddrStreet] = useState('');
    const [addrNumber, setAddrNumber] = useState('');
    const [addrComplement, setAddrComplement] = useState('');
    const [addrNeighborhood, setAddrNeighborhood] = useState('');
    const [addrCityUf, setAddrCityUf] = useState('');
    const [cepStatus, setCepStatus] = useState<'idle' | 'loading' | 'notfound'>('idle');
    const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
    /** Live conflict-check result per selected member (create form). */
    const [conflicts, setConflicts] = useState<Record<string, EventMemberConflict>>({});
    const [checkingConflicts, setCheckingConflicts] = useState(false);

    // View mode: clients for the participant lookup.
    const [clients, setClients] = useState<Client[]>([]);
    // Add-participant form.
    const [newName, setNewName] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newClientId, setNewClientId] = useState<string | null>(null);
    const [showClientList, setShowClientList] = useState(false);
    const [addingParticipant, setAddingParticipant] = useState(false);
    const [payingId, setPayingId] = useState<string | null>(null);

    const isCreate = eventId == null;

    // Load the event in view mode.
    useEffect(() => {
        if (!open || isCreate) { setEvent(null); setIsEditing(false); return; }
        setIsEditing(false);
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const token = await getAccessTokenSilently();
                const [e, cs] = await Promise.all([
                    getEventById(eventId!, token),
                    getAllClientsByBusinessId(businessId, token).catch(() => [] as Client[]),
                ]);
                if (!cancelled) { setEvent(e); setClients(cs); }
            } catch (err) {
                if (!cancelled) setAlert('danger', err instanceof Error ? err.message : t('events.alerts.failLoad'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, eventId, isCreate, businessId, getAccessTokenSilently, setAlert, t]);

    // Reset the create draft whenever the drawer opens in create mode.
    useEffect(() => {
        if (!open || !isCreate) return;
        setServiceId(''); setTitle('');
        const start = initialStart || '';
        setStartTime(start);
        setEndTime(start ? addMinutes(start, 60) : '');
        setCapacity(null); setDefaultPrice(0); setIsPublic(false); setNotes('');
        setDepositRequired(false); setDepositAmount(null);
        setHasLocation(false); setAddrCep(''); setAddrStreet(''); setAddrNumber('');
        setAddrComplement(''); setAddrNeighborhood(''); setAddrCityUf(''); setCepStatus('idle');
        setSelectedMembers([]); setConflicts({});
    }, [open, isCreate, initialStart]);

    // Live conflict check whenever the member list or the window changes.
    // On edit, the event itself is excluded from the check.
    const formActive = isCreate || isEditing;
    useEffect(() => {
        if (!formActive || selectedMembers.length === 0 || !startTime || !endTime) { setConflicts({}); return; }
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                setCheckingConflicts(true);
                const token = await getAccessTokenSilently();
                const rows = await checkEventConflicts(businessId, selectedMembers, startTime, endTime,
                    isCreate ? null : eventId, token);
                if (!cancelled) {
                    const map: Record<string, EventMemberConflict> = {};
                    rows.forEach((r) => { map[String(r.teamMemberId)] = r; });
                    setConflicts(map);
                }
            } catch { /* conflito é um aviso; falha silenciosa */ }
            finally { if (!cancelled) setCheckingConflicts(false); }
        }, 400);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [formActive, isCreate, eventId, selectedMembers, startTime, endTime, businessId, getAccessTokenSilently]);

    /** Duration (minutes) of the currently-picked event type; 60 as a fallback. */
    const durationMin = useMemo(() => {
        const svc = eventTypes.find((s) => s.id === serviceId);
        return svc?.totalDuration && svc.totalDuration > 0 ? Math.round(svc.totalDuration) : 60;
    }, [eventTypes, serviceId]);

    /** Human duration label, e.g. "1h 30min" / "45min". */
    const durationLabel = useMemo(() => {
        const h = Math.floor(durationMin / 60);
        const m = durationMin % 60;
        return `${h > 0 ? `${h}h ` : ''}${m > 0 || h === 0 ? `${m}min` : ''}`.trim();
    }, [durationMin]);

    const onPickType = (id: string) => {
        setServiceId(id);
        const svc = eventTypes.find((s) => s.id === id);
        if (!svc) return;
        setTitle((prev) => prev || svc.name || '');
        setDefaultPrice(svc.price ?? 0);
        setCapacity(svc.capacity ?? null);
        // Deposit is decided per event (below), NOT snapshotted from the type —
        // it can change between editions of the same workshop.
        // Recompute the end from the current start + THIS type's duration.
        const dur = svc.totalDuration && svc.totalDuration > 0 ? Math.round(svc.totalDuration) : 60;
        if (startTime) setEndTime(addMinutes(startTime, dur));
    };

    const memberName = (id: string) => {
        const m = teamMembers.find((x) => String(x.id) === String(id));
        return m ? `${m.firstName} ${m.lastName}`.trim() : `#${id}`;
    };

    const addableMembers = useMemo(
        () => teamMembers.filter((m) => !selectedMembers.includes(String(m.id))),
        [teamMembers, selectedMembers],
    );

    const isBR = canonicalCountry(country) === 'brazil';

    /** On CEP blur (Brazil), auto-fill the city/UF hint and prefill the street. */
    const handleCepBlur = async () => {
        if (!isCompletePostal(addrCep, country)) { setCepStatus('idle'); return; }
        setCepStatus('loading');
        const found = await lookupAddress(addrCep, country);
        if (found) {
            setAddrCityUf([found.city, found.state].filter(Boolean).join(' / '));
            // Prefill street + neighborhood from the CEP, but keep whatever the
            // user already typed (the address stays editable even with a CEP).
            setAddrStreet((prev) => prev || found.street || '');
            setAddrNeighborhood((prev) => prev || found.neighborhood || '');
            setCepStatus('idle');
        } else {
            setCepStatus('notfound');
        }
    };

    /** Assembled address string stored on the event (null when not used/empty).
     *  "Rua X, 123, Apto 4 — Bairro — Cidade / UF — CEP 00000-000" */
    const assembledLocation = (): string | null => {
        if (!hasLocation) return null;
        const street = [addrStreet.trim(), addrNumber.trim(), addrComplement.trim()]
            .filter(Boolean).join(', ');
        const parts = [
            street,
            addrNeighborhood.trim(),
            addrCityUf.trim(),
            addrCep.trim() ? `CEP ${addrCep.trim()}` : '',
        ].filter(Boolean);
        return parts.length ? parts.join(' — ') : null;
    };

    /** Best-effort inverse of assembledLocation — splits the stored string back
     *  into the separated fields so the address is editable. */
    const parseLocationInto = (loc: string | null | undefined) => {
        if (!loc || !loc.trim()) {
            setHasLocation(false);
            setAddrCep(''); setAddrStreet(''); setAddrNumber('');
            setAddrComplement(''); setAddrNeighborhood(''); setAddrCityUf('');
            return;
        }
        setHasLocation(true);
        let cep = ''; let cityUf = '';
        const rest: string[] = [];
        loc.split(' — ').map((s) => s.trim()).filter(Boolean).forEach((p) => {
            if (/^CEP\s+/i.test(p)) cep = p.replace(/^CEP\s+/i, '');
            else if (p.includes(' / ')) cityUf = p;
            else rest.push(p);
        });
        const streetParts = (rest[0] ?? '').split(',').map((s) => s.trim());
        setAddrCep(cep);
        setAddrCityUf(cityUf);
        setAddrStreet(streetParts[0] ?? '');
        setAddrNumber(streetParts[1] ?? '');
        setAddrComplement(streetParts.slice(2).join(', '));
        setAddrNeighborhood(rest[1] ?? '');
        setCepStatus('idle');
    };

    /** Switch to edit mode with the form prefilled from the loaded event. */
    const startEditing = () => {
        if (!event) return;
        setServiceId(event.serviceId);
        setTitle(event.title);
        setStartTime(event.startTime.slice(0, 16));
        setEndTime(event.endTime.slice(0, 16));
        setCapacity(event.capacity ?? null);
        setDefaultPrice(event.defaultPrice ?? 0);
        setIsPublic(!!event.isPublic);
        setDepositRequired(!!event.depositRequired);
        setDepositAmount(event.depositAmount ?? null);
        setNotes(event.notes ?? '');
        parseLocationInto(event.location);
        setSelectedMembers(event.teamMemberIds.map(String));
        setConflicts({});
        setIsEditing(true);
    };

    const handleUpdate = async () => {
        if (!event) return;
        if (!title.trim()) { setAlert('danger', t('events.alerts.titleRequired')); return; }
        if (!startTime || !endTime) { setAlert('danger', t('events.alerts.timeRequired')); return; }
        if (selectedMembers.length === 0) { setAlert('danger', t('events.alerts.memberRequired')); return; }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            const input: EventInput = {
                id: event.id, businessId, serviceId: event.serviceId,
                title: title.trim(), startTime, endTime,
                capacity, defaultPrice, isPublic, depositRequired, depositAmount,
                location: assembledLocation(), notes: notes.trim() || null,
                teamMemberIds: selectedMembers,
            };
            const updated = await updateEvent(input, token);
            setEvent(updated);
            setIsEditing(false);
            setAlert('success', t('events.alerts.updated'));
            onChanged();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('events.alerts.failUpdate'));
        } finally { setSaving(false); }
    };

    const handleCreate = async () => {
        if (!serviceId) { setAlert('danger', t('events.alerts.pickType')); return; }
        if (!title.trim()) { setAlert('danger', t('events.alerts.titleRequired')); return; }
        if (!startTime || !endTime) { setAlert('danger', t('events.alerts.timeRequired')); return; }
        if (selectedMembers.length === 0) { setAlert('danger', t('events.alerts.memberRequired')); return; }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            const input: EventInput = {
                businessId, serviceId, title: title.trim(), startTime, endTime,
                capacity, defaultPrice, isPublic, depositRequired, depositAmount,
                location: assembledLocation(), notes: notes.trim() || null,
                teamMemberIds: selectedMembers, participants: [],
            };
            await createEvent(input, token);
            setAlert('success', t('events.alerts.created'));
            onChanged(); onClose();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('events.alerts.failCreate'));
        } finally { setSaving(false); }
    };

    const reloadEvent = async () => {
        const token = await getAccessTokenSilently();
        setEvent(await getEventById(eventId!, token));
        onChanged();
    };

    const resetParticipantForm = () => {
        setNewName(''); setNewPhone(''); setNewEmail(''); setNewClientId(null); setShowClientList(false);
    };

    /** Clients matching the typed name/email/phone (participant lookup, Change 4). */
    const clientMatches = useMemo<Client[]>(() => {
        const q = newName.trim().toLowerCase();
        if (!q) return [];
        return clients.filter((c) => {
            const name = clientLabel(c).toLowerCase();
            return name.includes(q) || (c.email ?? '').toLowerCase().includes(q) || (c.phone ?? '').includes(q);
        }).slice(0, 8);
    }, [newName, clients]);

    const pickClient = (c: Client) => {
        setNewClientId(c.id);
        setNewName(clientLabel(c));
        setNewPhone(c.phone ?? '');
        setNewEmail(c.email ?? '');
        setShowClientList(false);
    };

    const handleAddParticipant = async () => {
        if (!event || !newName.trim()) { setAlert('danger', t('events.alerts.participantName')); return; }
        try {
            setAddingParticipant(true);
            const token = await getAccessTokenSilently();
            await addEventParticipant(event.id, {
                clientId: newClientId,
                customerName: newName.trim(),
                customerPhone: newPhone.trim() || null,
                customerEmail: newEmail.trim() || null,
            }, token);
            resetParticipantForm();
            await reloadEvent();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('events.alerts.failAddParticipant'));
        } finally { setAddingParticipant(false); }
    };

    const handleRemoveParticipant = async (p: EventParticipant) => {
        if (!(await confirmDialog({
            title: t('common.confirmTitle'),
            message: t('events.confirmRemoveParticipant', { name: p.customerName }),
            danger: true, confirmLabel: t('common.delete'),
        }))) return;
        try {
            const token = await getAccessTokenSilently();
            await removeEventParticipant(p.id, token);
            await reloadEvent();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('events.alerts.failRemoveParticipant'));
        }
    };

    const handlePayParticipant = async (p: EventParticipant) => {
        if (!event || payingId) return;
        const amount = p.price ?? event.defaultPrice ?? 0;
        if (amount <= 0) { setAlert('danger', t('events.alerts.noPrice')); return; }
        if (!(await confirmDialog({
            title: t('events.payTitle'),
            message: t('events.confirmPay', { name: p.customerName, amount: formatMoney(amount, currency) }),
            confirmLabel: t('events.payConfirm'),
        }))) return;
        try {
            setPayingId(p.id);
            const token = await getAccessTokenSilently();
            await recordManualPayment({
                businessId, eventId: event.id, eventParticipantId: p.id,
                clientId: p.clientId ?? null,
                customerName: p.customerName, amount, method: 'CASH',
                currency: currencyCode(currency),
                description: t('events.paymentDescription', { title: event.title }),
            }, token);
            setAlert('success', t('events.alerts.paid', { name: p.customerName }));
            await reloadEvent();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('events.alerts.failPay'));
        } finally { setPayingId(null); }
    };

    const handleCancelEvent = async () => {
        if (!event) return;
        const isSeries = !!event.seriesId;
        const wholeSeries = isSeries
            ? await confirmDialog({
                title: t('events.cancelTitle'), message: t('events.confirmCancelSeries'),
                confirmLabel: t('events.cancelWholeSeries'), cancelLabel: t('events.cancelThisOnly'),
            })
            : await confirmDialog({
                title: t('events.cancelTitle'), message: t('events.confirmCancel'),
                danger: true, confirmLabel: t('common.confirm'),
            });
        if (!isSeries && !wholeSeries) return;
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            await cancelEvent(event.id, null, isSeries ? wholeSeries : false, token);
            setAlert('success', t('events.alerts.cancelled'));
            onChanged(); onClose();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('events.alerts.failCancel'));
        } finally { setSaving(false); }
    };

    const paidCount = useMemo(
        () => (event?.participants ?? []).filter((p) => p.paymentStatus === 'PAID').length,
        [event],
    );

    const footer = isCreate ? (
        <>
            <button type="button" className="btn btn-primary" disabled={saving}
                onClick={handleCreate} data-cy="event-create-btn">
                {saving ? t('common.working') : t('events.create')}
            </button>
            <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={onClose}>
                {t('common.cancel')}
            </button>
        </>
    ) : isEditing ? (
        <>
            <button type="button" className="btn btn-primary" disabled={saving}
                onClick={handleUpdate} data-cy="event-update-btn">
                {saving ? t('common.working') : t('common.save')}
            </button>
            <button type="button" className="btn btn-outline-secondary" disabled={saving}
                onClick={() => setIsEditing(false)} data-cy="event-edit-cancel-btn">
                {t('common.cancel')}
            </button>
        </>
    ) : (
        <>
            {event && event.status !== 'CANCELLED' && (
                <button type="button" className="btn btn-primary" disabled={saving}
                    onClick={startEditing} data-cy="event-edit-btn">
                    {t('common.edit')}
                </button>
            )}
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                {t('common.close')}
            </button>
            {event && event.status !== 'CANCELLED' && (
                <button type="button" className="btn btn-outline-danger ms-auto" disabled={saving}
                    onClick={handleCancelEvent} data-cy="event-cancel-btn">
                    {t('events.cancelEvent')}
                </button>
            )}
        </>
    );

    return (
        <Drawer
            open={open}
            onClose={onClose}
            size="xl"
            title={isCreate ? t('events.drawerNew') : isEditing ? t('events.drawerEdit') : (event?.title ?? t('events.drawerTitle'))}
            footer={footer}
        >
            <div data-cy="event-drawer">
                {(isCreate || isEditing) ? (
                    <div className="event-create-form">
                        <div className="form-group">
                            <label>{t('events.type')} {isCreate && <span className="gs-required">*</span>}</label>
                            <select className="form-select" value={serviceId} disabled={isEditing}
                                onChange={(e) => onPickType(e.target.value)} data-cy="event-type-select">
                                <option value="">{t('events.pickTypePlaceholder')}</option>
                                {eventTypes.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                            </select>
                            {isCreate && eventTypes.length === 0 && (
                                <small className="form-text text-danger">{t('events.noTypes')}</small>
                            )}
                            {isEditing && (
                                <small className="form-text text-muted">{t('events.typeLockedHint')}</small>
                            )}
                        </div>

                        <div className="form-group">
                            <label>{t('events.title')} <span className="gs-required">*</span></label>
                            <input type="text" className="form-control" maxLength={255}
                                value={title} onChange={(e) => setTitle(e.target.value)} data-cy="event-title-input" />
                        </div>

                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label>{t('events.start')} <span className="gs-required">*</span></label>
                                <DateTimeField data-cy="event-start-input" value={startTime}
                                    stepMinutes={stepMinutes}
                                    onChange={(v) => {
                                        // Create: end follows start + the type's duration.
                                        // Edit: keep the event's CURRENT duration (it may
                                        // differ from the type's default).
                                        const dur = (isEditing && startTime && endTime)
                                            ? Math.max(5, Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60000))
                                            : durationMin;
                                        setStartTime(v);
                                        if (v) setEndTime(addMinutes(v, dur));
                                    }} />
                            </div>
                            <div className="col-md-6 form-group">
                                <label>{t('events.end')} <span className="gs-required">*</span></label>
                                <DateTimeField data-cy="event-end-input" value={endTime}
                                    stepMinutes={stepMinutes} onChange={setEndTime} />
                            </div>
                        </div>
                        {serviceId && (
                            <div className="form-text text-muted" data-cy="event-duration">
                                {t('events.duration', { duration: durationLabel })}
                            </div>
                        )}

                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label>{t('events.pricePerParticipant')}</label>
                                <MoneyInput value={String(defaultPrice)} currency={currency}
                                    dataCy="event-price-input"
                                    onChange={(v) => setDefaultPrice(v === '' ? 0 : Number(v))} />
                            </div>
                            <div className="col-md-6 form-group">
                                <label>{t('events.capacity')}</label>
                                <input type="text" inputMode="numeric" className="form-control" maxLength={5}
                                    value={capacity ?? ''} placeholder={t('events.capacityPlaceholder')}
                                    onChange={(e) => {
                                        const d = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
                                        setCapacity(d === '' ? null : Number(d));
                                    }} />
                            </div>
                        </div>

                        {/* Deposit — decided per event (moved here from the catalog).
                            Toggle + amount; the consumer pays it at join when required. */}
                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label className="gs-inline-check">
                                    <input type="checkbox" className="gs-inline-check__box"
                                        checked={depositRequired}
                                        // Platform flag: the deposit is charged online at join,
                                        // so it can't be required while Stripe is off.
                                        disabled={!ONLINE_PAYMENTS_ENABLED}
                                        onChange={(e) => {
                                            setDepositRequired(e.target.checked);
                                            if (!e.target.checked) setDepositAmount(null);
                                        }}
                                        data-cy="event-deposit-required" />
                                    <span className="gs-inline-check__label">{t('events.depositRequired')}</span>
                                </label>
                                <small className="form-text text-muted">
                                    {ONLINE_PAYMENTS_ENABLED ? t('events.depositHint') : t('events.depositDisabledHint')}
                                </small>
                            </div>
                            <div className="col-md-6 form-group">
                                <label>{t('events.depositAmount')}</label>
                                <MoneyInput value={depositAmount != null ? String(depositAmount) : ''}
                                    currency={currency} disabled={!depositRequired}
                                    dataCy="event-deposit-amount-input"
                                    onChange={(v) => setDepositAmount(v === '' ? null : Number(v))} />
                            </div>
                        </div>

                        {/* Professionals — add via searchable select, listed with a live
                            conflict badge (Change 3). */}
                        <div className="form-group">
                            <label>{t('events.professionals')} <span className="gs-required">*</span></label>
                            <SearchableSelect
                                dataCy="event-member-select"
                                options={addableMembers.map((m) => ({ value: String(m.id), label: `${m.firstName} ${m.lastName}`.trim() }))}
                                value={null}
                                placeholder={t('events.addProfessional')}
                                onChange={(id) => { if (id) setSelectedMembers((prev) => [...prev, id]); }}
                            />
                            <div className="event-member-list" data-cy="event-member-list">
                                {selectedMembers.length === 0 ? (
                                    <div className="empty-hint">{t('events.noProfessionals')}</div>
                                ) : selectedMembers.map((id) => {
                                    const c = conflicts[String(id)];
                                    const busy = c?.hasConflict;
                                    return (
                                        <div key={id} className="event-member-row" data-cy={`event-member-row-${id}`}>
                                            <span className="event-member-row__name">{memberName(id)}</span>
                                            {checkingConflicts && !c ? (
                                                <span className="event-member-row__status text-muted">{t('events.checking')}</span>
                                            ) : busy ? (
                                                <span className="event-member-row__status event-conflict" data-cy={`event-member-conflict-${id}`}>
                                                    ⚠ {c?.description || t('events.conflict')}
                                                </span>
                                            ) : (
                                                <span className="event-member-row__status event-free">✓ {t('events.free')}</span>
                                            )}
                                            <button type="button" className="btn btn-sm btn-link text-danger"
                                                onClick={() => setSelectedMembers((prev) => prev.filter((m) => m !== id))}>
                                                {t('common.remove')}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                            <small className="form-text text-muted">{t('events.professionalsHint')}</small>
                        </div>

                        {/* Public / private reminder. */}
                        <div className="form-group">
                            <label className="gs-inline-check">
                                <input type="checkbox" className="gs-inline-check__box"
                                    checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)}
                                    data-cy="event-public-toggle" />
                                <span className="gs-inline-check__label">{t('events.publicLabel')}</span>
                            </label>
                            <small className={`form-text ${isPublic ? 'text-primary' : 'text-muted'}`}>
                                {isPublic ? t('events.publicHint') : t('events.privateHint')}
                            </small>
                        </div>

                        {/* Own address (Change 2) — only when the event is not at the
                            business location. Country follows the business (no picker);
                            Brazil leads with the CEP, which auto-fills city/UF. */}
                        <div className="form-group">
                            <label className="gs-inline-check">
                                <input type="checkbox" className="gs-inline-check__box"
                                    checked={hasLocation} onChange={(e) => setHasLocation(e.target.checked)}
                                    data-cy="event-haslocation-toggle" />
                                <span className="gs-inline-check__label">{t('events.hasLocation')}</span>
                            </label>
                            {hasLocation && (
                                <div className="event-address" data-cy="event-address">
                                    <div className="row">
                                        <div className="col-md-5 form-group">
                                            <label>{isBR ? t('events.cep') : t('events.postalCode')}</label>
                                            <input type="text" className="form-control" inputMode="numeric" maxLength={20}
                                                value={formatPostal(addrCep, country)}
                                                placeholder={postalPlaceholder(country)}
                                                onChange={(e) => { setAddrCep(formatPostal(e.target.value, country)); setCepStatus('idle'); }}
                                                onBlur={handleCepBlur} data-cy="event-cep-input" />
                                            {cepStatus === 'loading' && <span className="form-text text-muted">{t('events.cepSearching')}</span>}
                                            {cepStatus === 'notfound' && <span className="form-text text-danger">{t('events.cepNotFound')}</span>}
                                        </div>
                                        <div className="col-md-7 form-group">
                                            <label>{t('events.cityUf')}</label>
                                            <input type="text" className="form-control" value={addrCityUf} readOnly={isBR} data-cy="event-cityuf-input"
                                                onChange={(e) => setAddrCityUf(e.target.value)}
                                                placeholder={isBR ? t('events.cityFromCep') : ''} />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-8 form-group">
                                            <label>{t('events.street')}</label>
                                            <input type="text" className="form-control" maxLength={300}
                                                value={addrStreet} onChange={(e) => setAddrStreet(e.target.value)}
                                                placeholder={t('events.streetPlaceholder')} data-cy="event-address-street" />
                                        </div>
                                        <div className="col-md-4 form-group">
                                            <label>{t('events.number')}</label>
                                            <input type="text" className="form-control" maxLength={20}
                                                value={addrNumber} onChange={(e) => setAddrNumber(e.target.value)}
                                                placeholder={t('events.numberPlaceholder')} data-cy="event-address-number" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 form-group">
                                            <label>{t('events.complement')}</label>
                                            <input type="text" className="form-control" maxLength={100}
                                                value={addrComplement} onChange={(e) => setAddrComplement(e.target.value)}
                                                placeholder={t('events.complementPlaceholder')} data-cy="event-address-complement" />
                                        </div>
                                        <div className="col-md-6 form-group">
                                            <label>{t('events.neighborhood')}</label>
                                            <input type="text" className="form-control" maxLength={100}
                                                value={addrNeighborhood} onChange={(e) => setAddrNeighborhood(e.target.value)}
                                                placeholder={t('events.neighborhoodPlaceholder')} data-cy="event-address-neighborhood" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <label>{t('events.notes')}</label>
                            <textarea className="form-control" rows={2} maxLength={2000}
                                value={notes} onChange={(e) => setNotes(e.target.value)} />
                        </div>
                    </div>
                ) : loading ? (
                    <p className="text-muted">{t('common.loading')}</p>
                ) : !event ? (
                    <p className="text-muted">{t('events.notFound')}</p>
                ) : (
                    <div className="event-view">
                        {event.status === 'CANCELLED' && (
                            <div className="alert alert-secondary py-2">{t('events.cancelledBadge')}</div>
                        )}
                        <div className="event-meta">
                            <div><strong>{t('events.when')}:</strong>{' '}
                                {formatBizDateTime(new Date(event.startTime), timeFmt, i18n.language)}
                                {' – '}
                                {formatClock(new Date(event.endTime), timeFmt)}</div>
                            <div><strong>{t('events.professionals')}:</strong>{' '}
                                {event.teamMemberIds.map(memberName).join(', ')}</div>
                            {event.location && (
                                <div><strong>{t('events.location')}:</strong> {event.location}</div>
                            )}
                            <div className={event.isPublic ? 'text-primary small' : 'text-muted small'}>
                                {event.isPublic ? t('events.isPublicBadge') : t('events.isPrivateBadge')}
                            </div>
                            {event.seriesId && <div className="text-muted small">{t('events.partOfSeries')}</div>}
                        </div>

                        <div className="event-money" data-cy="event-money">
                            <div className="event-money__box">
                                <span className="event-money__label">{t('events.total')}</span>
                                <span className="event-money__value">{formatMoney(event.totalPrice, currency)}</span>
                            </div>
                            <div className="event-money__box">
                                <span className="event-money__label">{t('events.alreadyPaid')}</span>
                                <span className="event-money__value" data-cy="event-already-paid">
                                    {formatMoney(event.alreadyPaid, currency)}
                                </span>
                            </div>
                            <div className="event-money__box">
                                <span className="event-money__label">{t('events.status')}</span>
                                <span className={`event-status event-status--${event.paymentStatus.toLowerCase()}`}
                                    data-cy="event-payment-status">
                                    {t(`events.paymentStatus.${event.paymentStatus}`)}
                                </span>
                            </div>
                        </div>

                        <div className="event-participants">
                            <div className="section-header d-flex justify-content-between align-items-center">
                                <span>{t('events.participants')}</span>
                                <span className="text-muted small">
                                    {event.participants.length}{event.capacity ? ` / ${event.capacity}` : ''} · {t('events.paidCount', { paid: paidCount })}
                                </span>
                            </div>

                            {event.status !== 'CANCELLED' && (
                                <div className="event-add-participant" data-cy="event-add-participant">
                                    <div className="gs-form-grid">
                                        <div className="event-client-search">
                                            <input type="text" className="form-control form-control-sm" maxLength={255}
                                                placeholder={t('events.participantSearchPlaceholder')} value={newName}
                                                onChange={(e) => { setNewName(e.target.value); setNewClientId(null); setShowClientList(true); }}
                                                onFocus={() => setShowClientList(true)}
                                                data-cy="event-participant-name" />
                                            {showClientList && clientMatches.length > 0 && (
                                                <ul className="event-client-dropdown" data-cy="event-client-dropdown">
                                                    {clientMatches.map((c) => (
                                                        <li key={c.id} onMouseDown={() => pickClient(c)}>
                                                            <span>{clientLabel(c)}</span>
                                                            {(c.email || c.phone) && <small className="text-muted"> · {c.email || c.phone}</small>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                        <input type="text" className="form-control form-control-sm" maxLength={50}
                                            placeholder={t('events.participantPhonePlaceholder')} value={newPhone}
                                            onChange={(e) => setNewPhone(e.target.value)} />
                                        <input type="email" className="form-control form-control-sm" maxLength={255}
                                            placeholder={t('events.participantEmailPlaceholder')} value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)} />
                                        <button type="button" className="btn btn-sm btn-outline-primary"
                                            disabled={addingParticipant || !newName.trim()}
                                            onClick={handleAddParticipant} data-cy="event-add-participant-btn">
                                            {addingParticipant ? t('common.working') : t('events.addParticipant')}
                                        </button>
                                    </div>
                                    {newClientId && <small className="form-text text-success">{t('events.clientLinked')}</small>}
                                </div>
                            )}

                            {event.participants.length === 0 ? (
                                <div className="empty-hint">{t('events.noParticipants')}</div>
                            ) : (
                                <table className="event-participant-table" data-cy="event-participant-table">
                                    <thead>
                                        <tr>
                                            <th>{t('events.participant')}</th>
                                            <th>{t('events.price')}</th>
                                            <th>{t('events.status')}</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {event.participants.map((p) => {
                                            const price = p.price ?? event.defaultPrice ?? 0;
                                            const paid = p.paymentStatus === 'PAID';
                                            return (
                                                <tr key={p.id} data-cy={`event-participant-${p.id}`}>
                                                    <td>
                                                        <div>{p.customerName}{p.clientId ? ' 👤' : ''}</div>
                                                        {(p.customerPhone || p.customerEmail) && (
                                                            <small className="text-muted">{p.customerPhone || p.customerEmail}</small>
                                                        )}
                                                    </td>
                                                    <td>{formatMoney(price, currency)}</td>
                                                    <td>
                                                        <span className={`event-status event-status--${paid ? 'paid' : 'pending'}`}
                                                            data-cy={`event-participant-status-${p.id}`}>
                                                            {paid ? t('events.paid') : t('events.pending')}
                                                        </span>
                                                    </td>
                                                    <td className="event-participant-actions">
                                                        {event.status !== 'CANCELLED' && !paid && (
                                                            <button type="button" className="btn btn-sm btn-primary"
                                                                disabled={payingId === p.id}
                                                                onClick={() => handlePayParticipant(p)}
                                                                data-cy={`event-pay-${p.id}`}>
                                                                {payingId === p.id ? t('common.working') : t('events.pay')}
                                                            </button>
                                                        )}
                                                        {event.status !== 'CANCELLED' && !paid && (
                                                            <button type="button" className="btn btn-sm btn-link text-danger"
                                                                onClick={() => handleRemoveParticipant(p)}>
                                                                {t('common.delete')}
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Drawer>
    );
};

export default EventDrawer;
